// @flow

import { fromJS, Map, Record } from "immutable"
import _ from "lodash"
import fp from "lodash/fp"

import cuckoo from "common/services/cuckoo/cuckoo"
import { cuckooEvents } from "common/consts/enums"
import { mapToUtcMoment } from "common/services/momentHelpers/momentHelpers"
import type { ScheduleChangeInput } from "mmb/records/bookingInput"
import mapperFlightsHistory from "./mapperFlightsHistory"

export type EmergencyAirline = "PF" | "6F" | "CO" | "WW" | "9W" | "ZI"

export const SHOT_EMERGENCY_AIRLINES: Array<EmergencyAirline> = ["PF", "6F", "CO", "WW", "9W", "ZI"]

const addIndexToJourney = journeys => journeys.map((journey, index) => ({ ...journey, index }))
const sortAlternativesByPrice = journeys =>
  journeys.length ? journeys.sort((a, b) => a.price.paid_by_pap - b.price.paid_by_pap) : journeys

// $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
const mapShotAlternatives = fp.compose(
  mapperFlightsHistory,
  addIndexToJourney,
  sortAlternativesByPrice,
)

const Resolved = Record({
  type: null,
  timestamp: null,
})

const Refund = Record({
  type: "full",
  amount: 0,
  currency: "eur",
  refundForm: false,
})

export default function(args: ScheduleChangeInput) {
  cuckoo.track(cuckooEvents.MMB_SHOT_RECEIVED, { data: args })
  // NOTE: if schedule change is live & also, if it's the root mmb call
  if (typeof args.is_live !== "undefined" && args.href)
    return Map({ live: args.is_live, action: args.action })

  const alternatives = _.isEmpty(args.alternatives)
    ? args.alternatives
    : mapShotAlternatives(args.alternatives)

  const changes = {}
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
  _.map(args.changes, (change, id) => {
    let amendedChange
    if (change) {
      amendedChange = {
        ...change,
        departure: {
          where: change.departure.where,
          when: {
            local: mapToUtcMoment(change.departure.when.local),
            utc: mapToUtcMoment(change.departure.when.utc),
          },
        },
        arrival: {
          where: change.arrival.where,
          when: {
            local: mapToUtcMoment(change.arrival.when.local),
            utc: mapToUtcMoment(change.arrival.when.utc),
          },
        },
        number: change.flight_number ? Number(change.flight_number) : null,
      }
    } else {
      amendedChange = change
    }
    changes[id] = amendedChange
  })

  const refund = args.refund
    ? new Refund({
        ...args.refund,
        currency: args.refund.currency.toLowerCase(),
      })
    : new Refund()

  const resolved =
    args.resolved &&
    new Resolved({
      type: args.resolved.type,
      timestamp: mapToUtcMoment(args.resolved.timestamp),
    })

  const currentJourney =
    args.current_trip &&
    mapperFlightsHistory([
      {
        trips: args.current_trip,
      },
    ]).first()

  return fromJS({
    action: args.action,
    affected: args.affected,
    alternatives,
    changes,
    allowedGates: args.allowed_gates,
    id: args.alternative_group_id,
    refund,
    resolved,
    currentJourney,
    validUntil: args.valid_until ? mapToUtcMoment(args.valid_until).local() : null,
    isGuaranteeCase: ["KIWI", "PAX", "CS"].includes(args.action), // it's false if it's AERO or INFO
    contactCS: Boolean(args.action === "CS"),
    hasConvenientAlternative: args.has_convenient_alternative,
    flightsChecked: args.flights_checked === null ? true : Boolean(args.flights_checked), // can be null - it means there are no alternatives and never will be
    emergencyAirlines: SHOT_EMERGENCY_AIRLINES,
    scheduleChangeProcessed: Boolean(args.schedule_change_processed),
  })
}
